"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { DotPattern } from "@/components/ui/dot-pattern";

interface DotGlowProps {
  children: React.ReactNode;
  className?: string;
  /** How far the dots extend beyond the container (Tailwind inset value) */
  inset?: string;
  /** Percentage where the fade starts (0-100) */
  fadeStart?: number;
  /** Delay in ms before the dots fade in after scroll trigger */
  delay?: number;
}

export function DotGlow({
  children,
  className,
  inset = "32",
  fadeStart = 50,
  delay = 300,
}: DotGlowProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setIsVisible(true);
            }, delay);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, [delay]);

  return (
    <div ref={ref} className={cn("relative", className)}>
      <div
        className={cn(
          "absolute pointer-events-none transition-opacity duration-700 ease-out",
          `-inset-${inset}`,
          isVisible ? "opacity-100" : "opacity-0"
        )}
        style={{
          maskImage: `radial-gradient(farthest-side at center, white ${fadeStart}%, transparent 100%)`,
          WebkitMaskImage: `radial-gradient(farthest-side at center, white ${fadeStart}%, transparent 100%)`,
        }}
      >
        <DotPattern
          width={20}
          height={20}
          cx={2}
          cy={2}
          cr={2}
          glow={true}
        />
      </div>
      {children}
    </div>
  );
}
